import type { IconComponent, IconContainerVariant, IconContainerSize } from '@/types';

interface IconContainerProps {
  /** Компонент иконки (lucide-react или кастомный SVG) */
  icon?: IconComponent;
  /** Эмодзи как альтернатива иконке */
  emoji?: string;
  /** Короткий текст (напр. код валюты USD) как альтернатива иконке/эмодзи */
  text?: string;
  /** Цветовая схема из палитры Accent Colors или системные цвета */
  variant: IconContainerVariant;
  /** Размер контейнера: default (40×40px) | large (48×48px) */
  size?: IconContainerSize;
  /** Размер иконки в пикселях (по умолчанию: 22px для default, 28px для large) */
  iconSize?: number;
  /** Толщина линий иконки (для lucide-иконок) */
  strokeWidth?: number;
  /** Дополнительные CSS классы */
  className?: string;
}

// Маппинг вариантов на CSS переменные
const variantStyles: Record<IconContainerVariant, { bg: string; icon: string }> = {
  blue: {
    bg: 'var(--blue-light)',
    icon: 'var(--blue-dark)',
  },
  orange: {
    bg: 'var(--orange-light)',
    icon: 'var(--orange-dark)',
  },
  pink: {
    bg: 'var(--pink-light)',
    icon: 'var(--pink-dark)',
  },
  purple: {
    bg: 'var(--purple-light)',
    icon: 'var(--purple-dark)',
  },
  yellow: {
    bg: 'var(--yellow-light)',
    icon: 'var(--yellow-dark)',
  },
  red: {
    bg: 'var(--red-light)',
    icon: 'var(--red-dark)',
  },
  green: {
    bg: 'var(--green-light)',
    icon: 'var(--green-dark)',
  },
  accent: {
    bg: 'var(--purple-light)',
    icon: 'var(--purple-dark)',
  },
  destructive: {
    bg: 'var(--red-light)',
    icon: 'var(--red-dark)',
  },
  overlay: {
    bg: 'rgba(255, 255, 255, 0.2)',
    icon: 'var(--white)',
  },
};

export function IconContainer({
  icon: Icon,
  emoji,
  text,
  variant,
  size = 'default',
  iconSize,
  strokeWidth,
  className = '',
}: IconContainerProps): JSX.Element {
  const styles = variantStyles[variant];
  const containerSize = size === 'large' ? 'w-12 h-12' : 'w-10 h-10';
  const defaultIconSize = size === 'large' ? 24 : 22;
  const emojiSize = size === 'large' ? 'text-3xl' : 'text-xl';
  const textSize = size === 'large' ? 'text-base' : 'text-xs';

  return (
    <div
      className={`flex items-center justify-center flex-shrink-0 ${containerSize} ${className}`}
      style={{
        borderRadius: 'var(--radius-sm)',
        backgroundColor: styles.bg,
      }}
    >
      {Icon ? (
        <Icon
          size={iconSize || defaultIconSize}
          style={{ color: styles.icon }}
          strokeWidth={strokeWidth}
        />
      ) : emoji ? (
        <span
          className={emojiSize}
          style={{ color: styles.icon }}
        >
          {emoji}
        </span>
      ) : text ? (
        <span
          className={`${textSize} font-semibold`}
          style={{ color: styles.icon }}
        >
          {text}
        </span>
      ) : null}
    </div>
  );
}